/**
 * The device's IANA time zone, sent to the API so "this month" means the user's month.
 *
 * ## Why the client has to say
 *
 * The API runs on DigitalOcean in UTC. Every monthly figure it returns used to be windowed with
 * server-local dates, so "Revenue This Month" reset at UTC midnight for everyone - 6:00 PM on the
 * last of the month in Mountain time, which is how a user in Boise watched the number go to
 * $0.00 several hours before their month was over (reported 2026-08-31).
 *
 * The server cannot work the zone out on its own. IP geolocation is a guess, and there is no
 * stored profile zone; the browser, meanwhile, already knows the answer exactly and for free.
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` is the device's *current* zone, which is the
 * right definition here - a user who flies to another zone should see their new month roll over
 * on the calendar their phone is showing them, with no profile edit involved.
 *
 * The header is advisory. The backend validates it and falls back to UTC (see the API's
 * `src/utils/month-range.ts`), so a browser that returns nothing usable is simply no worse off
 * than it was before this existed.
 */

/** Header name; must match `TIMEZONE_HEADER` in the API's `src/utils/month-range.ts`. */
export const TIMEZONE_HEADER = 'X-Timezone';

/**
 * The device's IANA zone, or `null` when the runtime cannot name one.
 *
 * Read on every call rather than cached at module load: a long-lived session can cross a zone
 * boundary (a flight, a laptop moving between offices), and re-reading costs one `Intl` lookup.
 */
export function getClientTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.length > 0 ? zone : null;
  } catch {
    return null;
  }
}

/**
 * `{ 'X-Timezone': '<zone>' }`, or `{}` when there is nothing to send.
 *
 * Spread into a `headers` object. Returning an empty object rather than a header with an empty
 * value keeps the backend's "no signal" branch and its "unparseable signal" branch the same one.
 */
export function timezoneHeaders(): Record<string, string> {
  const zone = getClientTimeZone();
  return zone ? { [TIMEZONE_HEADER]: zone } : {};
}
